import { useCallback, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Alert, Box, Typography } from '@mui/material';
import * as state from '@/state';
import { CuboidClickMode } from '@/types';
import { textButtonStyle } from '@/theme';
import AddIcon from '@mui/icons-material/Add';
import { OutlinedButton } from './styles';
import { CuboidBase, CuboidState } from '../../types';

type AddCuboidButtonProps<CuboidT extends CuboidBase> = {
  cuboidState: CuboidState<CuboidT>;
};

export const AddCuboidButton = <CuboidT extends CuboidBase>({
  cuboidState,
}: AddCuboidButtonProps<CuboidT>) => {
  const [cuboidClickMode, setCuboidClickMode] = useAtom(cuboidState.cuboidClickMode);
  const setIsCuboidEditorEnabled = useSetAtom(cuboidState.isCuboidEditorEnabled);

  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const selectedSpherical = useAtomValue(state.selectedSpherical);

  const handleAddCuboid = useCallback(() => {
    // save current cuboid
    setIsCuboidEditorEnabled(false);
    setCuboidClickMode((mode) =>
      mode === CuboidClickMode.AddCuboid ? CuboidClickMode.Default : CuboidClickMode.AddCuboid
    );
  }, [setCuboidClickMode, setIsCuboidEditorEnabled]);

  useEffect(() => {
    switch (cuboidClickMode) {
      case CuboidClickMode.AddCuboid:
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="info">Please click on Point cloud to place a new cuboid</Alert>,
        });
        break;
      default:
    }
  }, [cuboidClickMode, setSnackbarMessage]);

  const focusBoxStyle =
    cuboidClickMode === CuboidClickMode.AddCuboid
      ? {
          borderColor: '#6C64EC',
          borderWidth: '3px',
          borderStyle: 'solid',
          borderRadius: '6px',
        }
      : {
          padding: '3px',
        };

  return (
    <Box sx={focusBoxStyle}>
      <OutlinedButton
        sx={textButtonStyle}
        variant="outlined"
        startIcon={<AddIcon style={{ color: '#fff' }} />}
        disabled={!!selectedSpherical}
        onClick={handleAddCuboid}
      >
        <Typography color={'#fff'}>Add Cuboid</Typography>
      </OutlinedButton>
    </Box>
  );
};
